// Тонкая типизированная обёртка над Telegram WebApp SDK (telegram-web-app.js,
// подключён в index.html). Всё — best-effort: вне Telegram (обычный браузер) методы
// деградируют без ошибок, чтобы приложение можно было открыть и в dev.

export interface TgWebAppUser {
  id: number
  first_name?: string
  last_name?: string
  username?: string
}

interface TgWebApp {
  initData: string
  initDataUnsafe?: { user?: TgWebAppUser; start_param?: string }
  colorScheme?: 'light' | 'dark'
  ready: () => void
  expand: () => void
  setHeaderColor?: (color: string) => void
  setBackgroundColor?: (color: string) => void
  requestContact?: (callback: (result: unknown) => void) => void
  openLink?: (url: string, options?: { try_instant_view?: boolean }) => void
  BackButton?: {
    show: () => void
    hide: () => void
    onClick: (cb: () => void) => void
    offClick: (cb: () => void) => void
  }
  HapticFeedback?: { notificationOccurred?: (type: 'error' | 'success' | 'warning') => void }
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TgWebApp }
  }
}

/** Экземпляр WebApp или null (вне Telegram). */
export function tg(): TgWebApp | null {
  return (typeof window !== 'undefined' && window.Telegram?.WebApp) || null
}

/** Открыты ли мы внутри Telegram (есть подписанная initData). */
export function isTelegram(): boolean {
  return !!tg()?.initData
}

/**
 * Сырая initData для заголовка X-Telegram-Init-Data. В dev (вне Telegram) можно
 * подставить захваченную реальную строку через VITE_DEV_INIT_DATA, чтобы тестировать
 * в обычном браузере.
 */
export function initDataRaw(): string {
  const fromTg = tg()?.initData
  if (fromTg) return fromTg
  return (import.meta.env.VITE_DEV_INIT_DATA as string | undefined) ?? ''
}

export function tgUser(): TgWebAppUser | null {
  return tg()?.initDataUnsafe?.user ?? null
}

/** start_param диплинка (t.me/bot?startapp=<slug>) — если через него открыли. */
export function startParam(): string | null {
  const p = tg()?.initDataUnsafe?.start_param
  return p && p.trim() ? p.trim() : null
}

/** Инициализация: ready + expand + подгон цветов под тёмную тему приложения. */
export function initTelegram(): void {
  const w = tg()
  if (!w) return
  try {
    w.ready()
    w.expand()
    // Приложение всегда тёмное (Casino Noir) — фон/шапку красим под него, чтобы
    // не было светлых полос в клиентах со светлой темой.
    w.setHeaderColor?.('#0f1216')
    w.setBackgroundColor?.('#0f1216')
  } catch {
    // no-op
  }
}

/** Нативная кнопка «Назад» Telegram. */
export function showBackButton(cb: () => void): void {
  const b = tg()?.BackButton
  if (!b) return
  b.onClick(cb)
  b.show()
}

export function hideBackButton(cb?: () => void): void {
  const b = tg()?.BackButton
  if (!b) return
  if (cb) b.offClick(cb)
  b.hide()
}

/**
 * Внешняя ссылка: внутри Telegram — во встроенном браузере (обычный target=_blank
 * в WebView срабатывает не во всех клиентах), вне Telegram — новой вкладкой.
 */
export function openLink(url: string): void {
  const w = tg()
  if (w && typeof w.openLink === 'function') w.openLink(url)
  else window.open(url, '_blank', 'noopener,noreferrer')
}

/**
 * Нативный запрос контакта (телефона). Возвращает true, если пользователь поделился
 * номером (телефон уходит боту → бэкенд сохраняет в tg_contacts; сам номер в приложение
 * НЕ приходит — это ограничение Telegram, поэтому телефон потом перечитываем через API).
 * false — отказ / метод недоступен (старый клиент).
 */
export function requestContact(): Promise<boolean> {
  const w = tg()
  return new Promise((resolve) => {
    if (!w || typeof w.requestContact !== 'function') {
      resolve(false)
      return
    }
    try {
      w.requestContact((result: unknown) => {
        if (typeof result === 'boolean') {
          resolve(result)
        } else if (result && typeof result === 'object' && 'status' in result) {
          resolve((result as { status?: string }).status === 'sent')
        } else {
          resolve(false)
        }
      })
    } catch {
      resolve(false)
    }
  })
}
